'use client';

/**
 * **Show the reader's own choice until the write it caused has landed** (VALE-355).
 *
 * The instruction sub-forms commit straight to the server — there is no Save button —
 * and every control in them was *controlled by the record*. So a click did not land
 * visibly until the `PUT` came back: the chip stayed unselected, the ownership row
 * kept its old label, and the rows that depend on either arrived a round-trip later,
 * in stages. Reported first as *"sometimes we click, it doesn't select"* and then as
 * *"it's really not smooth when we switch from one ownership to another"*.
 *
 * This holds the reader's intent in front of the record for exactly as long as the
 * write is in flight. Three rules, and each of the first two was learned by getting
 * it wrong:
 *
 * · **Drop the intent when the record MATCHES it, not when the record changes.**
 *   Clearing on any change meant the first of two in-flight responses tore the mask
 *   away and the control snapped back to a state the reader had already moved on
 *   from — visible as a control flipping by itself.
 * · **…and only once nothing is outstanding.** Matching alone is not enough: after
 *   two quick clicks that cancel out, the intent equals the record's *pre-click*
 *   value, so a match-only guard fires while both writes are still in flight and the
 *   first response shows through anyway.
 * · **A rejected write must release the mask outright**, or a failed commit strands
 *   the surface showing something the record does not hold — for good, since the
 *   record can never come to match a change that did not happen.
 *
 * It is deliberately **not** a general-purpose optimistic cache. It masks latency on
 * one control; the record stays the only durable truth, and the mask is gone the
 * moment it would start disagreeing with it.
 */

import { useState } from 'react';

export interface OptimisticValue<T> {
  /** What the control should render — the pending intent, else the record. */
  shown: T;
  /**
   * Record an intent and run the write that realises it. `run` returns the commit's
   * promise where the host has one (the drawer writes to the server; the add dialog
   * writes to a buffer and returns nothing) — both settle, so the mask lifts either
   * on the response or on the next tick.
   */
  apply: (next: T, run: () => void | Promise<unknown>) => void;
}

export function useOptimisticValue<T>(
  committed: T,
  /** Value equality. Defaults to `Object.is`; pass one for arrays or objects. */
  isEqual: (a: T, b: T) => boolean = Object.is
): OptimisticValue<T> {
  const [intent, setIntent] = useState<{ value: T } | null>(null);
  const [pending, setPending] = useState(0);

  /*
    A render-phase `setState` guarded by a condition that is false on the next
    render — the pattern this folder already uses for `pendingPaidOutOfPocket`, and
    preferred over an effect for the same reason: it reacts to an actual transition
    rather than scheduling a second render pass after every one.
  */
  if (pending === 0 && intent !== null && isEqual(intent.value, committed)) {
    setIntent(null);
  }

  return {
    shown: intent ? intent.value : committed,
    apply: (next, run) => {
      setIntent({ value: next });
      setPending((n) => n + 1);
      const done = () => setPending((n) => Math.max(0, n - 1));
      void Promise.resolve(run()).then(done, () => {
        /*
          **A rejected write releases the mask outright, not via the match test
          above.** The match can never fire for a change that did not happen, so
          waiting for it left the surface showing a value the record never took —
          permanently. Caught by this hook's own spec, which is the whole reason that
          case is written down.

          Dropping the intent is the honest outcome: the write failed, so the record
          is the truth, and the row the reader touched reverts in front of them
          rather than lying quietly.
        */
        done();
        setIntent(null);
      });
    }
  };
}

/** Order-insensitive equality for the key lists {@link useOptimisticValue} masks. */
export function sameKeySet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sorted = [...a].sort();
  return [...b].sort().every((key, index) => key === sorted[index]);
}
